import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  on,
  Show,
} from "solid-js";
import { useNavigate, useSearchParams } from "@solidjs/router";
import type {
  MeasurementPoint,
  PointReading,
  RuleInput,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t, tOr } from "../i18n.ts";
import {
  formatDateTime,
  formatNumber,
  formatRange,
  formatShare,
} from "../format.ts";
import { pathTo, range, scope, siteTree } from "../context.ts";
import { subtreeOf } from "../tree.ts";
import {
  average,
  duration,
  heatmap,
  linearFit,
  pairs,
  stats,
  STEPS,
  toCsv,
  total,
  typicalProfile,
  anomalies,
  perDegreeHour,
  type Sample,
  type Step,
} from "../analysis.ts";
import { pointSeries, type PointSeries } from "../pointData.ts";
import { isFull, zoomAt, type Span } from "../chartZoom.ts";
import { Icon, type IconName } from "../icons.tsx";
import {
  Chart,
  chartPng,
  Navigator,
  type ChartMode,
  type ChartSeries,
} from "../components/Chart.tsx";
import { MEDIA_IDS, MediumBadge, mediumColor } from "../components/Media.tsx";
import { unitLabel } from "../components/PointCard.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import {
  Dialog,
  operator,
  PageHeader,
  RenameDialog,
  Skeleton,
  useAction,
} from "../components/ui.tsx";
import { RuleWizard } from "../components/RuleWizard.tsx";
import { MeterParameters } from "../components/MeterParameters.tsx";
import { AddToReport } from "../components/AddToReport.tsx";
import { newRule } from "../events.ts";

const TYPES: readonly { key: AnalysisType; icon: IconName }[] = [
  { key: "line", icon: "chart" },
  { key: "cmp", icon: "swap" },
  { key: "heat", icon: "calendar" },
  { key: "dur", icon: "gauge" },
  { key: "xy", icon: "thermo" },
  { key: "obj", icon: "building" },
  { key: "par", icon: "bolt" },
];
type AnalysisType = "line" | "cmp" | "heat" | "dur" | "xy" | "obj" | "par";
const DAY = 86_400_000;
const TYPICAL_WEEKS = 4;
const STEP_MS: Record<Step, number> = {
  "15m": 900_000,
  "1h": 3_600_000,
  "6h": 21_600_000,
  "1d": 86_400_000,
};
const SAVED_KEY = "ep.analyses";
const color = (i: number) => `var(--s${(i % 6) + 1})`;

interface Saved {
  name: string;
  points: string[];
  type: AnalysisType;
}

function readSaved(): Saved[] {
  try {
    return JSON.parse(localStorage.getItem(SAVED_KEY) ?? "[]") as Saved[];
  } catch {
    return [];
  }
}

function writeSaved(all: Saved[]): void {
  try {
    localStorage.setItem(SAVED_KEY, JSON.stringify(all));
  } catch {
    // Not remembered in this browser; the analysis still works.
  }
}

const defaultStep = (from: Date, to: Date): Step => {
  const days = (to.getTime() - from.getTime()) / 86_400_000;
  return days <= 2 ? "15m" : days <= 14 ? "1h" : days <= 62 ? "6h" : "1d";
};

/**
 * Analyses (prototype "Analiza zużycia"): any points of the scope over the period, as series in panels per unit, a
 * comparison with the previous period, a week heat map, the load duration curve and a model against temperature,
 * with statistics per point. Scope and period come from the context bar.
 */
export function AnalysisPage() {
  // A link may open a given analysis of a given point (e.g. the meter parameters from My places).
  const [params] = useSearchParams<{ type?: string; point?: string }>();
  const [query, setQuery] = createSignal("");
  const [selected, setSelected] = createSignal<string[]>(
    params.point ? [params.point] : [],
  );
  const [type, setType] = createSignal<AnalysisType>(
    TYPES.some((x) => x.key === params.type)
      ? (params.type as AnalysisType)
      : "line",
  );
  const [step, setStep] = createSignal<Step>(
    defaultStep(range().from, range().to),
  );
  createEffect(() => setStep(defaultStep(range().from, range().to)));
  const [hidden, setHidden] = createSignal<ReadonlySet<string>>(new Set());
  // Shared by every panel and the navigator; a new period shows its whole range.
  const [view, setView] = createSignal<Span | null>(null);
  createEffect(
    on(
      () => [range().from.getTime(), range().to.getTime()],
      () => setView(null),
      { defer: true },
    ),
  );
  const [mode, setMode] = createSignal<ChartMode>("select");
  const [layers, setLayers] = createSignal<{
    typical: boolean;
    anomalies: boolean;
  }>({ typical: true, anomalies: true });
  const [context, setContext] = createSignal<"" | "temp">("");
  const [norm, setNorm] = createSignal<"" | "dd">("");
  const [tempId, setTempId] = createSignal<string>();
  const [highlight, setHighlight] = createSignal<string | null>(null);
  const [noteAt, setNoteAt] = createSignal<number>();
  const svgs = new Map<string, SVGSVGElement>();
  const [saved, setSaved] = createSignal<Saved[]>(readSaved());

  const nodes = () => siteTree()?.nodes ?? [];
  const candidates = createMemo(() => {
    const id = scope()?.id;
    const inside = new Set(
      id ? subtreeOf(nodes(), id) : nodes().map((n) => n.id),
    );
    return (siteTree()?.points ?? []).filter(
      (p) => inside.has(p.nodeId) && p.binding,
    );
  });
  const byId = createMemo(
    () => new Map((siteTree()?.points ?? []).map((p) => [p.id, p])),
  );
  const roots = () =>
    nodes()
      .filter((n) => n.parentId === null)
      .map((n) => n.id);
  const [readings] = createResource(
    () => (siteTree() ? roots().join() : undefined),
    async () =>
      new Map(
        (await Promise.all(roots().map((id) => api.sites.pointsNow(id))))
          .flat()
          .map((r) => [r.pointId, r]),
      ),
  );
  const chosen = () => selected().filter((id) => byId().has(id));
  const colorOf = (id: string) => color(chosen().indexOf(id));

  const load = (
    points: MeasurementPoint[],
    from: Date,
    to: Date,
    rd: Map<string, PointReading>,
  ) =>
    Promise.all(points.map((p) => pointSeries(p, rd.get(p.id), from, to))).then(
      (all) => all.filter((x): x is PointSeries => x !== null),
    );
  const [data] = createResource(
    () =>
      readings() && chosen().length > 0
        ? { ids: chosen().join(), ...range() }
        : undefined,
    (q) =>
      load(
        q.ids.split(",").map((id) => byId().get(id)!),
        q.from,
        q.to,
        readings()!,
      ),
  );
  const previous = () => {
    const len = range().to.getTime() - range().from.getTime();
    return { from: new Date(range().from.getTime() - len), to: range().from };
  };
  const [before] = createResource(
    () =>
      readings() && type() === "cmp" && chosen().length > 0
        ? { ids: chosen().join(), ...previous() }
        : undefined,
    (q) =>
      load(
        q.ids.split(",").map((id) => byId().get(id)!),
        q.from,
        q.to,
        readings()!,
      ),
  );

  // Annotations and events of the chosen points, drawn on the time axis.
  const [notes, { refetch: refetchNotes }] = createResource(
    () =>
      chosen().length > 0 ? { ids: chosen().join(), ...range() } : undefined,
    (q) => api.annotations.list(q.ids.split(","), q.from, q.to),
  );
  const [events] = createResource(() => api.events.list());
  const navigate = useNavigate();
  const markers = createMemo(() =>
    (events() ?? [])
      .filter(
        (e) => e.subjectType === "point" && chosen().includes(e.subjectId),
      )
      .map((e) => ({
        at: new Date(e.startedAt).getTime(),
        label: t(`events.title.${e.kind}`, { name: e.subjectName }),
        onClick: () => navigate(`/events/${encodeURIComponent(e.id)}`),
      })),
  );
  // The typical course needs earlier weeks: fetched for at most two series, as in the prototype.
  const [earlier] = createResource(
    () =>
      readings() &&
      type() === "line" &&
      (layers().typical || layers().anomalies) &&
      chosen().length > 0 &&
      chosen().length <= 2
        ? { ids: chosen().join(), from: range().from }
        : undefined,
    (q) =>
      load(
        q.ids.split(",").map((id) => byId().get(id)!),
        new Date(q.from.getTime() - TYPICAL_WEEKS * 7 * DAY),
        q.from,
        readings()!,
      ),
  );
  const profiles = createMemo(
    () =>
      new Map(
        (earlier() ?? []).map((s) => [s.point.id, typicalProfile(s.values)]),
      ),
  );
  // Outdoor or room temperature as context: any temperature point of the scope.
  const temperatures = createMemo(() =>
    candidates().filter(
      (p) => readings()?.get(p.id)?.attribute === "temperature",
    ),
  );
  const tempPoint = () =>
    temperatures().find((p) => p.id === tempId()) ?? temperatures()[0];
  const [tempSeries] = createResource(
    () =>
      readings() && (context() === "temp" || norm() === "dd") && tempPoint()
        ? { id: tempPoint()!.id, ...range() }
        : undefined,
    async (q) =>
      (await load([byId().get(q.id)!], q.from, q.to, readings()!))[0] ?? null,
  );
  const zoom = (factor: number) => {
    const full = { from: range().from.getTime(), to: range().to.getTime() };
    const v = view() ?? full;
    const next = zoomAt(
      v,
      full,
      (v.from + v.to) / 2,
      factor,
      Math.max(3_600_000, STEP_MS[step()] * 4),
    );
    setView(isFull(next, full) ? null : next);
  };
  // A click on a day (or six hours) shows its hours.
  const drill = (at: number) => {
    if (step() === "1d" || step() === "6h") {
      const d = new Date(at);
      const from = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
      ).getTime();
      setView({ from, to: from + DAY });
      setStep("1h");
    }
  };
  const exportPng = () => {
    const first = [...svgs.values()][0];
    if (first) {
      chartPng(first, `analiza-${range().from.toISOString().slice(0, 10)}`);
    }
  };
  const filtered = () => {
    const q = query().trim().toLocaleLowerCase();
    return candidates().filter(
      (p) => q === "" || p.name.toLocaleLowerCase().includes(q),
    );
  };
  const toggle = (id: string) =>
    setSelected(
      chosen().includes(id)
        ? chosen().filter((x) => x !== id)
        : [...chosen(), id],
    );
  const quick = (pred: (p: MeasurementPoint) => boolean) =>
    setSelected(
      candidates()
        .filter(pred)
        .slice(0, 8)
        .map((p) => p.id),
    );
  const path = (p: MeasurementPoint) =>
    pathTo(nodes(), p.nodeId)
      .slice(1)
      .map((n) => n.name)
      .join(" / ");

  const [naming, setNaming] = createSignal(false);
  const [adding, setAdding] = createSignal(false);
  const [ruling, setRuling] = createSignal<RuleInput>();
  // Prototype "Utwórz regułę alarmu": a threshold at 90 % of the first selected point's maximum in the period.
  const proposeRule = () => {
    const s = data()?.[0];
    if (!s) {
      return;
    }
    const max = Math.max(
      ...s.values.filter((v) => v.v !== null).map((v) => v.v!),
    );
    const threshold = Number.isFinite(max)
      ? Math.round(max * 0.9 * 100) / 100
      : null;
    setRuling({
      ...newRule(
        s.point.id,
        t("events.wizard.aboveName", {
          name: s.point.name,
          value: formatNumber(threshold, locale(), 2),
        }),
      ),
      threshold,
    });
  };
  const save = (name: string) => {
    const next = [
      ...saved().filter((s) => s.name !== name.trim()),
      { name: name.trim(), points: chosen(), type: type() },
    ];
    setSaved(next);
    writeSaved(next);
    return Promise.resolve();
  };
  const exportCsv = () => {
    const series = (data() ?? []).map((s) => ({
      name: `${s.point.name} [${unitLabel(s.unit)}]`,
      samples: average(s.values, step()),
    }));
    const blob = new Blob([`﻿${toCsv(series)}`], {
      type: "text/csv;charset=utf-8",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `analiza-${range().from.toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <PageHeader
        eyebrow={`${t("analysis.eyebrow")} · ${scope()?.name ?? ""} · ${formatRange(range().from, range().to, locale())}`}
        title={t("analysis.title")}
        sub={t("analysis.intro")}
      >
        <button
          type="button"
          class="btn"
          disabled={chosen().length === 0}
          onClick={() => setNaming(true)}
        >
          <Icon name="save" />
          {t("analysis.save")}
        </button>
        <button
          type="button"
          class="btn"
          disabled={!data()?.length}
          onClick={exportCsv}
        >
          <Icon name="file" />
          {t("analysis.export")}
        </button>
        <Show when={type() !== "par"}>
          <button
            type="button"
            class="btn"
            disabled={chosen().length === 0}
            onClick={() => setAdding(true)}
          >
            <Icon name="file" />
            {t("analysis.toReport")}
          </button>
        </Show>
        <Show when={operator()}>
          <button
            type="button"
            class="btn"
            disabled={!data()?.length}
            onClick={proposeRule}
          >
            <Icon name="bell" />
            {t("analysis.toRule")}
          </button>
        </Show>
      </PageHeader>
      <Show when={adding()}>
        <AddToReport
          series={chosen().map((id) => ({
            source: "point" as const,
            pointId: id,
            attribute: null,
            channel: null,
          }))}
          title={chosen()
            .map((id) => byId().get(id)?.name ?? "")
            .filter((x) => x)
            .join(", ")
            .slice(0, 120)}
          onClose={() => setAdding(false)}
        />
      </Show>
      <div class="an">
        <section class="card">
          <div class="panel-h">
            <label class="search">
              <Icon name="search" />
              <input
                type="search"
                placeholder={t("analysis.search")}
                value={query()}
                autocomplete="off"
                onInput={(e) => setQuery(e.currentTarget.value)}
              />
            </label>
          </div>
          <div
            style={{
              padding: "8px 12px",
              "border-bottom": "1px solid var(--line)",
            }}
          >
            <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
              {t("analysis.quick")}
            </div>
            <div class="views">
              <button
                type="button"
                class="vchip"
                onClick={() =>
                  quick((p) => p.role === "main" || p.role === "source")
                }
              >
                {t("analysis.quickBoundary")}
              </button>
              <button
                type="button"
                class="vchip"
                onClick={() =>
                  quick((p) => p.role === "sub" || p.role === "load")
                }
              >
                {t("analysis.quickLoads")}
              </button>
              <button
                type="button"
                class="vchip"
                onClick={() => quick((p) => p.role === "environment")}
              >
                {t("analysis.quickConditions")}
              </button>
              <Show when={chosen().length > 0}>
                <button
                  type="button"
                  class="vchip"
                  onClick={() => setSelected([])}
                >
                  {t("common.clear")}
                </button>
              </Show>
            </div>
          </div>
          <div
            style={{
              "max-height": "520px",
              overflow: "auto",
              "padding-bottom": "6px",
            }}
          >
            <For
              each={MEDIA_IDS.filter((m) =>
                filtered().some((p) => p.medium === m),
              )}
              fallback={<div class="card-b hint">{t("analysis.noPoints")}</div>}
            >
              {(m) => (
                <>
                  <div class="sgh">
                    <MediumBadge medium={m} />
                    {t(`medium.${m}`)}
                  </div>
                  <For each={filtered().filter((p) => p.medium === m)}>
                    {(p) => {
                      const on = () => chosen().includes(p.id);
                      return (
                        <label class="srow">
                          <input
                            type="checkbox"
                            checked={on()}
                            onChange={() => toggle(p.id)}
                          />
                          <span
                            class="sw2"
                            style={{
                              background: on() ? colorOf(p.id) : "transparent",
                              border: `1px solid ${on() ? colorOf(p.id) : "var(--line-2)"}`,
                            }}
                          />
                          <span style={{ "min-width": 0, flex: 1 }}>
                            <span style={{ display: "block" }}>{p.name}</span>
                            <span class="path">{path(p)}</span>
                          </span>
                        </label>
                      );
                    }}
                  </For>
                </>
              )}
            </For>
          </div>
          <div
            style={{
              "border-top": "1px solid var(--line)",
              padding: "8px 12px 12px",
            }}
          >
            <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
              {t("analysis.saved")}
            </div>
            <For
              each={saved()}
              fallback={
                <p class="hint" style={{ margin: 0 }}>
                  {t("analysis.noSaved")}
                </p>
              }
            >
              {(s) => (
                <div style={{ display: "flex", gap: "4px" }}>
                  <button
                    type="button"
                    class="btn ghost sm"
                    style={{
                      display: "flex",
                      flex: 1,
                      "justify-content": "flex-start",
                    }}
                    onClick={() => {
                      setSelected(s.points);
                      setType(s.type);
                    }}
                  >
                    <Icon
                      name={
                        TYPES.find((x) => x.key === s.type)?.icon ?? "chart"
                      }
                    />
                    {s.name}
                  </button>
                  <button
                    type="button"
                    class="btn ghost sm"
                    aria-label={t("common.delete")}
                    onClick={() => {
                      const next = saved().filter((x) => x.name !== s.name);
                      setSaved(next);
                      writeSaved(next);
                    }}
                  >
                    <Icon name="x" />
                  </button>
                </div>
              )}
            </For>
            <p class="hint" style={{ margin: "6px 0 0" }}>
              {t("analysis.savedHint")}
            </p>
          </div>
        </section>
        <div class="grid" style={{ gap: "16px", "min-width": 0 }}>
          <section class="card">
            <div
              class="types"
              role="group"
              aria-label={t("analysis.typeLabel")}
            >
              <For each={TYPES}>
                {(x) => (
                  <button
                    type="button"
                    aria-pressed={type() === x.key}
                    onClick={() => setType(x.key)}
                  >
                    <Icon name={x.icon} />
                    {t(`analysis.type.${x.key}`)}
                  </button>
                )}
              </For>
            </div>
            <Show when={type() === "line" || type() === "cmp"}>
              <div class="note" style={{ "flex-wrap": "wrap", gap: "10px" }}>
                <div
                  class="seg"
                  role="group"
                  aria-label={t("overview.resolution")}
                >
                  <For each={STEPS}>
                    {(st) => (
                      <button
                        type="button"
                        aria-pressed={step() === st}
                        onClick={() => setStep(st)}
                      >
                        {t(`analysis.step.${st}`)}
                      </button>
                    )}
                  </For>
                </div>
                <div class="seg" role="group" aria-label={t("analysis.zoom")}>
                  <button
                    type="button"
                    aria-label={t("analysis.zoomOut")}
                    onClick={() => zoom(2)}
                  >
                    −
                  </button>
                  <button
                    type="button"
                    aria-label={t("analysis.zoomIn")}
                    onClick={() => zoom(0.5)}
                  >
                    +
                  </button>
                  <Show when={view()}>
                    <button type="button" onClick={() => setView(null)}>
                      {t("chart.wholeRange")}
                    </button>
                  </Show>
                </div>
                <Show when={type() === "line"}>
                  <div
                    class="seg"
                    role="group"
                    aria-label={t("analysis.mouse")}
                  >
                    <For each={["select", "pan", "note"] as const}>
                      {(m) => (
                        <button
                          type="button"
                          aria-pressed={mode() === m}
                          title={t(`analysis.mouseHint.${m}`)}
                          onClick={() => setMode(m)}
                        >
                          {t(`analysis.mouseMode.${m}`)}
                        </button>
                      )}
                    </For>
                  </div>
                  <div
                    class="seg"
                    role="group"
                    aria-label={t("analysis.export")}
                  >
                    <button
                      type="button"
                      disabled={!data()?.length}
                      onClick={exportCsv}
                    >
                      CSV
                    </button>
                    <button
                      type="button"
                      disabled={!data()?.length}
                      onClick={exportPng}
                    >
                      PNG
                    </button>
                  </div>
                </Show>
              </div>
              <Show when={type() === "line"}>
                <div
                  class="note"
                  style={{ "flex-wrap": "wrap", gap: "10px", "border-top": 0 }}
                >
                  <div
                    class="seg"
                    role="group"
                    aria-label={t("analysis.context")}
                  >
                    <span class="hint" style={{ padding: "5px 6px" }}>
                      {t("analysis.context")}
                    </span>
                    <button
                      type="button"
                      aria-pressed={context() === ""}
                      onClick={() => setContext("")}
                    >
                      {t("analysis.ctx.none")}
                    </button>
                    <button
                      type="button"
                      disabled
                      title={t("analysis.productionSoon")}
                    >
                      {t("analysis.ctx.production")}
                    </button>
                    <button
                      type="button"
                      aria-pressed={context() === "temp"}
                      disabled={temperatures().length === 0}
                      onClick={() => setContext("temp")}
                    >
                      {t("analysis.ctx.temp")}
                    </button>
                  </div>
                  <div
                    class="seg"
                    role="group"
                    aria-label={t("analysis.normalize")}
                  >
                    <span class="hint" style={{ padding: "5px 6px" }}>
                      {t("analysis.per")}
                    </span>
                    <button
                      type="button"
                      aria-pressed={norm() === ""}
                      onClick={() => setNorm("")}
                    >
                      —
                    </button>
                    <button
                      type="button"
                      disabled
                      title={t("analysis.productionSoon")}
                    >
                      {t("analysis.norm.unit")}
                    </button>
                    <button
                      type="button"
                      aria-pressed={norm() === "dd"}
                      disabled={temperatures().length === 0}
                      onClick={() => setNorm("dd")}
                    >
                      {t("analysis.norm.dd")}
                    </button>
                  </div>
                  <Show
                    when={
                      (context() === "temp" || norm() === "dd") &&
                      temperatures().length > 1
                    }
                  >
                    <label class="hint">
                      {t(
                        norm() === "dd"
                          ? "analysis.tempPointDd"
                          : "analysis.tempPoint",
                      )}{" "}
                      <select
                        class="inp"
                        value={tempPoint()?.id ?? ""}
                        onChange={(e) => setTempId(e.currentTarget.value)}
                      >
                        <For each={temperatures()}>
                          {(tp) => <option value={tp.id}>{tp.name}</option>}
                        </For>
                      </select>
                    </label>
                  </Show>
                  <div class="views" style={{ "margin-left": "auto" }}>
                    <button
                      type="button"
                      class="mchip plain"
                      aria-pressed={layers().typical}
                      onClick={() =>
                        setLayers({ ...layers(), typical: !layers().typical })
                      }
                    >
                      {t("analysis.layer.typical")}
                    </button>
                    <button
                      type="button"
                      class="mchip plain"
                      disabled
                      title={t("analysis.forecastSoon")}
                      style={{ opacity: 0.5 }}
                    >
                      {t("analysis.layer.forecast")}
                    </button>
                    <button
                      type="button"
                      class="mchip plain"
                      aria-pressed={layers().anomalies}
                      onClick={() =>
                        setLayers({
                          ...layers(),
                          anomalies: !layers().anomalies,
                        })
                      }
                    >
                      {t("analysis.layer.anomalies")}
                    </button>
                  </div>
                </div>
              </Show>
            </Show>
            <Show when={type() === "obj"}>
              <Objects />
            </Show>
            <Show when={type() === "par"}>
              <MeterParameters points={candidates()} preferred={chosen()[0]} />
            </Show>
            <Show
              when={chosen().length > 0 && type() !== "obj" && type() !== "par"}
              fallback={
                <Show when={type() !== "obj" && type() !== "par"}>
                  <div class="card-b hint">{t("analysis.pick")}</div>
                </Show>
              }
            >
              <Show
                when={!data.error}
                fallback={<ErrorBox error={data.error} />}
              >
                <Show
                  when={data()}
                  fallback={
                    <div class="card-b">
                      <Skeleton lines={6} />
                    </div>
                  }
                >
                  {(d) => (
                    <>
                      <Show when={type() === "line"}>
                        <Lines
                          data={d()}
                          step={step()}
                          hidden={hidden()}
                          colorOf={colorOf}
                          view={view()}
                          onView={setView}
                          mode={mode()}
                          onNote={setNoteAt}
                          onDrill={drill}
                          notes={(notes() ?? []).map((a) => ({
                            at: new Date(a.at).getTime(),
                            text: a.text,
                            who: a.authorName,
                          }))}
                          markers={markers()}
                          highlight={highlight()}
                          profiles={
                            layers().typical || layers().anomalies
                              ? profiles()
                              : new Map()
                          }
                          typical={layers().typical}
                          anomalies={layers().anomalies}
                          temperature={
                            context() === "temp" ? (tempSeries() ?? null) : null
                          }
                          perDegreeHour={
                            norm() === "dd" ? (tempSeries() ?? null) : null
                          }
                          onSvg={(key, el) => svgs.set(key, el)}
                        />
                        <div class="lg">
                          <For each={d()}>
                            {(sr) => (
                              <button
                                type="button"
                                aria-pressed={!hidden().has(sr.point.id)}
                                onMouseEnter={() => setHighlight(sr.point.id)}
                                onMouseLeave={() => setHighlight(null)}
                                onClick={() => {
                                  const next = new Set(hidden());
                                  if (next.has(sr.point.id)) {
                                    next.delete(sr.point.id);
                                  } else {
                                    next.add(sr.point.id);
                                  }
                                  setHidden(next);
                                }}
                              >
                                <i
                                  style={{ background: colorOf(sr.point.id) }}
                                />
                                {sr.point.name}
                              </button>
                            )}
                          </For>
                          <span class="hint">{t("analysis.lineHint")}</span>
                        </div>
                        <Show
                          when={
                            chosen().length > 2 &&
                            (layers().typical || layers().anomalies)
                          }
                        >
                          <div
                            class="note"
                            style={{ "border-top": "1px solid var(--line)" }}
                          >
                            <Icon name="alert" />
                            {t("analysis.typicalLimit")}
                          </div>
                        </Show>
                      </Show>
                      <Show when={type() === "cmp"}>
                        <Compare now={d()} before={before()} step={step()} />
                      </Show>
                      <Show when={type() === "heat"}>
                        <Heat data={d()} />
                      </Show>
                      <Show when={type() === "dur"}>
                        <DurationCurve data={d()} colorOf={colorOf} />
                      </Show>
                      <Show when={type() === "xy"}>
                        <Temperature data={d()} />
                      </Show>
                    </>
                  )}
                </Show>
              </Show>
            </Show>
          </section>
          <Show
            when={
              data() &&
              data()!.length > 0 &&
              type() !== "xy" &&
              type() !== "obj"
            }
          >
            <StatsTable data={data()!} colorOf={colorOf} />
          </Show>
        </div>
      </div>
      <Show when={ruling()}>
        {(r) => (
          <RuleWizard
            initial={r()}
            onClose={() => setRuling(undefined)}
            onSaved={() => api.invalidate("/api/v1/alert-rules")}
          />
        )}
      </Show>
      <Show when={noteAt()}>
        {(at) => (
          <NoteDialog
            at={at()}
            pointIds={chosen()}
            onClose={() => setNoteAt(undefined)}
            onSaved={() => {
              api.invalidate("/api/v1/annotations");
              void refetchNotes();
            }}
          />
        )}
      </Show>
      <Show when={naming()}>
        <RenameDialog
          title={t("analysis.saveName")}
          value={t(`analysis.type.${type()}`)}
          onSave={save}
          onClose={() => setNaming(false)}
        />
      </Show>
    </>
  );
}

/**
 * One chart per unit with a common time axis (kW is never added to °C), all sharing the zoom and the mouse mode,
 * with annotations, events, the typical course and anomalies of up to two series, temperature as context, energy
 * per heating degree-hour, and a navigator of the whole period.
 */
function Lines(props: {
  data: PointSeries[];
  step: Step;
  hidden: ReadonlySet<string>;
  colorOf: (id: string) => string;
  view: Span | null;
  onView: (view: Span | null) => void;
  mode: ChartMode;
  onNote: (at: number) => void;
  onDrill: (at: number) => void;
  notes: { at: number; text: string; who: string }[];
  markers: { at: number; label: string; onClick?: () => void }[];
  highlight: string | null;
  profiles: Map<string, (t: number) => number | null>;
  typical: boolean;
  anomalies: boolean;
  temperature: PointSeries | null;
  perDegreeHour: PointSeries | null;
  onSvg: (key: string, el: SVGSVGElement) => void;
}) {
  const groups = createMemo(() => {
    const out = new Map<string, PointSeries[]>();
    for (const s of props.data) {
      out.set(s.unit, [...(out.get(s.unit) ?? []), s]);
    }
    return [...out];
  });
  const points = (s: PointSeries) =>
    average(s.values, props.step).map((p) => ({ t: p.t, v: p.v, c: p.c }));
  const bands = (series: PointSeries[]) =>
    props.anomalies
      ? series.flatMap((s) => {
          const profile = props.profiles.get(s.point.id);
          if (!profile) {
            return [];
          }
          const vals = s.values
            .filter((v) => v.v !== null)
            .map((v) => Math.abs(v.v!));
          const floor = vals.length ? Math.max(...vals) * 0.1 : 0;
          return anomalies(s.values, profile, 1.5, floor).map((b) => ({
            ...b,
            color: "var(--anom)",
          }));
        })
      : [];
  const typical = (s: PointSeries): ChartSeries[] => {
    const profile = props.profiles.get(s.point.id);
    if (!props.typical || !profile) {
      return [];
    }
    const pts = average(s.values, props.step).map((p) => ({
      t: p.t,
      v: profile(p.t),
    }));
    return pts.some((p) => p.v !== null)
      ? [
          {
            key: `typ-${s.point.id}`,
            label: t("analysis.typicalOf", { name: s.point.name }),
            color: props.colorOf(s.point.id),
            kind: "line",
            dotted: true,
            points: pts,
          },
        ]
      : [];
  };
  const extra = createMemo(() => {
    const out: {
      key: string;
      label: string;
      unit: string;
      series: ChartSeries[];
    }[] = [];
    if (props.temperature) {
      out.push({
        key: "ctx",
        label: t("analysis.contextOf", { name: props.temperature.point.name }),
        unit: unitLabel(props.temperature.unit),
        series: [
          {
            key: "ctx",
            label: props.temperature.point.name,
            color: "var(--m-temp)",
            kind: "line",
            points: points(props.temperature),
          },
        ],
      });
    }
    if (props.perDegreeHour) {
      const temp = props.perDegreeHour;
      const series = props.data
        .filter((s) => s.energy.length > 0)
        .slice(0, 3)
        .map<ChartSeries>((s) => ({
          key: `dd-${s.point.id}`,
          label: `${s.point.name} / °C·h`,
          color: props.colorOf(s.point.id),
          kind: "line",
          points: perDegreeHour(s.energy, temp.values),
        }));
      if (series.length > 0) {
        out.push({
          key: "dd",
          label: t("analysis.perDegreeHourTitle"),
          unit: "kWh/°C·h",
          series,
        });
      }
    }
    return out;
  });
  const chart = (
    key: string,
    label: string,
    unit: string,
    series: ChartSeries[],
    band: { from: number; to: number; color?: string }[],
    panels: number,
  ) => (
    <>
      <div class="panel-lbl">
        <b style={{ "font-weight": 500, color: "var(--ink)" }}>{label}</b>
      </div>
      <div class="card-b" style={{ "padding-bottom": "4px" }}>
        <Chart
          series={series}
          from={range().from}
          to={range().to}
          unit={unit}
          locale={locale()}
          bucketMs={STEP_MS[props.step]}
          emptyText={t("overview.noData")}
          now={Date.now()}
          nowLabel={t("overview.nowMark")}
          height={panels > 1 ? 180 : 260}
          view={props.view}
          onView={props.onView}
          mode={props.mode}
          onNote={props.onNote}
          onDrill={props.onDrill}
          annotations={props.notes}
          markers={props.markers}
          highlight={props.highlight}
          bands={band}
          bare
          onSvg={(el) => props.onSvg(key, el)}
        />
      </div>
    </>
  );
  const panels = () => groups().length + extra().length;
  return (
    <>
      <For each={groups()}>
        {([unit, series]) =>
          chart(
            unit,
            `${tOr(`attribute.${series[0]!.attribute}`, series[0]!.attribute)} · ${unitLabel(unit)}`,
            unitLabel(unit),
            series
              .filter((s) => !props.hidden.has(s.point.id))
              .flatMap<ChartSeries>((s) => [
                {
                  key: s.point.id,
                  label: s.point.name,
                  color: props.colorOf(s.point.id),
                  kind: "line",
                  points: points(s),
                },
                ...typical(s),
              ]),
            bands(series.filter((s) => !props.hidden.has(s.point.id))),
            panels(),
          )
        }
      </For>
      <For each={extra()}>
        {(e) => chart(e.key, e.label, e.unit, e.series, [], panels())}
      </For>
      <div class="card-b" style={{ "padding-top": 0 }}>
        <Navigator
          points={
            props.data[0]
              ? average(
                  props.data[0].values,
                  props.step === "15m" ? "1h" : props.step,
                ).map((p) => ({ t: p.t, v: p.v }))
              : []
          }
          from={range().from}
          to={range().to}
          view={props.view}
          onView={props.onView}
          hint={
            props.view
              ? t("analysis.navigatorPart")
              : t("analysis.navigatorAll")
          }
        />
      </div>
      <div class="legend" style={{ padding: "0 14px 10px" }}>
        <Show when={props.typical && props.profiles.size > 0}>
          <span>
            <i class="sw dash" style={{ "border-top-color": "var(--faint)" }} />
            {t("analysis.layer.typical")}
          </span>
        </Show>
        <Show when={props.anomalies && props.profiles.size > 0}>
          <span>
            <i class="sw box" style={{ background: "var(--anom)" }} />
            {t("analysis.layer.anomalies")}
          </span>
        </Show>
        <span>
          <i class="sw box" style={{ background: "var(--crit)" }} />
          {t("analysis.legendEvents")}
        </span>
        <span>
          <i class="sw box" style={{ background: "var(--accent)" }} />
          {t("analysis.legendNotes")}
        </span>
      </div>
      <Show when={groups().length > 1}>
        <div class="note" style={{ "border-top": "1px solid var(--line)" }}>
          <Icon name="alert" />
          {t("analysis.unitsApart")}
        </div>
      </Show>
    </>
  );
}

/** Adds an annotation at a moment to the chosen points (prototype "Dodaj adnotację do osi czasu"). */
function NoteDialog(props: {
  at: number;
  pointIds: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [text, setText] = createSignal("");
  const [mine, setMine] = createSignal(false);
  const [run, error, busy] = useAction();
  const save = () =>
    void run(async () => {
      await api.annotations.add({
        at: new Date(props.at),
        text: text().trim(),
        pointIds: props.pointIds,
        private: mine(),
      });
      props.onSaved();
      props.onClose();
    });
  return (
    <Dialog
      title={t("analysis.note.title", {
        at: formatDateTime(new Date(props.at), locale()),
      })}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !text().trim()}
            onClick={save}
          >
            {t("analysis.note.add")}
          </button>
        </>
      }
    >
      <div class="stack">
        <div class="field">
          <label for="noteText">{t("analysis.note.text")}</label>
          <input
            id="noteText"
            maxLength={500}
            value={text()}
            placeholder={t("analysis.note.placeholder")}
            onInput={(e) => setText(e.currentTarget.value)}
          />
        </div>
        <div class="field">
          <label for="noteVis">{t("analysis.note.visibility")}</label>
          <select
            id="noteVis"
            value={mine() ? "me" : "all"}
            onChange={(e) => setMine(e.currentTarget.value === "me")}
          >
            <option value="all">{t("analysis.note.everyone")}</option>
            <option value="me">{t("analysis.note.me")}</option>
          </select>
        </div>
        <p class="hint" style={{ margin: 0 }}>
          {t("analysis.note.hint")}
        </p>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}

/**
 * Places of the scope compared (prototype "Porównanie obiektów"): energy drawn in the period by the metered outlets
 * of each child place, from the flows of the scope; the unmetered rest is shown apart and not given to any place.
 */
function Objects() {
  const [perArea, setPerArea] = createSignal(false);
  const [report] = createResource(
    () => (scope() ? { id: scope()!.id, ...range() } : undefined),
    (q) =>
      api.sites.flows(
        q.id,
        q.from,
        q.to,
        (q.to.getTime() - q.from.getTime()) / DAY <= 2 ? "hour" : "day",
      ),
  );
  const rows = createMemo(() => {
    const r = report();
    const tree = siteTree();
    if (!r || !tree || !scope()) {
      return [];
    }
    const children = tree.nodes.filter((n) => n.parentId === scope()!.id);
    const pointNode = new Map(tree.points.map((p) => [p.id, p.nodeId]));
    const totals = new Map<string, number>();
    for (const o of r.outlets) {
      const node = pointNode.get(o.pointId);
      const child = children.find(
        (c) => node !== undefined && subtreeOf(tree.nodes, c.id).includes(node),
      );
      const key = child?.id ?? scope()!.id;
      totals.set(key, (totals.get(key) ?? 0) + (o.kwh ?? 0));
    }
    return [...totals]
      .map(([id, kwh]) => ({
        id,
        name:
          id === scope()!.id
            ? t("analysis.obj.direct", { name: scope()!.name })
            : (tree.nodes.find((n) => n.id === id)?.name ?? id),
        kwh,
      }))
      .sort((a, b) => b.kwh - a.kwh);
  });
  const max = () => Math.max(0.001, ...rows().map((r) => r.kwh));
  return (
    <>
      <div class="note" style={{ "flex-wrap": "wrap", gap: "10px" }}>
        <div class="seg" role="group" aria-label={t("analysis.normalize")}>
          <button
            type="button"
            aria-pressed={!perArea()}
            onClick={() => setPerArea(false)}
          >
            kWh
          </button>
          <button type="button" disabled title={t("analysis.obj.areaSoon")}>
            kWh/m²
          </button>
        </div>
      </div>
      <Show when={!report.error} fallback={<ErrorBox error={report.error} />}>
        <Show
          when={report()}
          fallback={
            <div class="card-b">
              <Skeleton lines={4} />
            </div>
          }
        >
          {(r) => (
            <div class="card-b" style={{ display: "grid", gap: "12px" }}>
              <Show
                when={r().hasBoundary}
                fallback={
                  <p class="hint" style={{ margin: 0 }}>
                    {t("overview.noBoundary")}
                  </p>
                }
              >
                <For
                  each={rows()}
                  fallback={
                    <p class="hint" style={{ margin: 0 }}>
                      {t("flows.noOutlets")}
                    </p>
                  }
                >
                  {(row, i) => (
                    <div
                      style={{
                        display: "grid",
                        "grid-template-columns": "170px 1fr 110px",
                        gap: "10px",
                        "align-items": "center",
                      }}
                    >
                      <span>{row.name}</span>
                      <div
                        style={{
                          height: "22px",
                          background: "var(--sunken)",
                          "border-radius": "4px",
                        }}
                      >
                        <div
                          style={{
                            height: "22px",
                            "border-radius": "4px",
                            width: `${(row.kwh / max()) * 100}%`,
                            background: color(i()),
                          }}
                        />
                      </div>
                      <span class="num" style={{ "text-align": "right" }}>
                        {formatNumber(row.kwh, locale(), 1)} kWh
                      </span>
                    </div>
                  )}
                </For>
                <p class="hint" style={{ margin: 0 }}>
                  {t("analysis.obj.hint", {
                    unmetered: formatNumber(r().unmetered, locale(), 1),
                  })}
                </p>
              </Show>
            </div>
          )}
        </Show>
      </Show>
    </>
  );
}

const energyOf = (s: PointSeries) =>
  s.energy.length > 0 ? s.energy.reduce((a, x) => a + (x.v ?? 0), 0) : null;
const nightShare = (s: PointSeries) => {
  const e = s.energy.filter((x) => x.v !== null);
  const all = e.reduce((a, x) => a + x.v!, 0);
  const night = e.filter((x) => {
    // Energy samples are labelled with the start of their bucket.
    const h = new Date(x.t).getHours();
    return h >= 22 || h < 6;
  });
  return all > 0 ? night.reduce((a, x) => a + x.v!, 0) / all : null;
};

/** This period against the previous one of the same length, for the points in the first unit. */
function Compare(props: {
  now: PointSeries[];
  before: PointSeries[] | undefined;
  step: Step;
}) {
  const unit = () => props.now[0]!.unit;
  const same = (all: PointSeries[]) => all.filter((s) => s.unit === unit());
  const len = () => range().to.getTime() - range().from.getTime();
  const sum = (all: PointSeries[], shift: number): Sample[] => {
    const acc = new Map<number, number | null>();
    for (const s of same(all)) {
      for (const p of average(s.values, props.step)) {
        const k = p.t + shift;
        const prev = acc.get(k) ?? null;
        acc.set(k, p.v === null ? prev : (prev ?? 0) + p.v);
      }
    }
    return [...acc].sort(([a], [b]) => a - b).map(([t, v]) => ({ t, v }));
  };
  const totalOf = (all: PointSeries[]) => {
    const e = same(all).map(energyOf);
    return e.some((x) => x === null) ? null : e.reduce((a, x) => a! + x!, 0);
  };
  const nightOf = (all: PointSeries[]) => {
    const e = same(all);
    const tot = e.reduce((a, s) => a + (energyOf(s) ?? 0), 0);
    return tot > 0
      ? e.reduce((a, s) => a + (energyOf(s) ?? 0) * (nightShare(s) ?? 0), 0) /
          tot
      : null;
  };
  const change = () => {
    const a = totalOf(props.now);
    const b = props.before ? totalOf(props.before) : null;
    return a !== null && b !== null && b > 0 ? a / b - 1 : null;
  };
  return (
    <Show
      when={props.before}
      fallback={
        <div class="card-b">
          <Skeleton lines={5} />
        </div>
      }
    >
      {(before) => (
        <>
          <div class="card-b">
            <Chart
              series={[
                {
                  key: "now",
                  label: t("analysis.thisPeriod"),
                  color: "var(--s2)",
                  kind: "line",
                  points: sum(props.now, 0),
                },
                {
                  key: "before",
                  label: t("analysis.previousPeriod"),
                  color: "var(--faint)",
                  kind: "line",
                  dashed: true,
                  points: sum(before(), len()),
                },
              ]}
              from={range().from}
              to={range().to}
              unit={unitLabel(unit())}
              locale={locale()}
              bucketMs={STEP_MS[props.step]}
              emptyText={t("overview.noData")}
            />
          </div>
          <div class="card-b">
            <table class="tbl">
              <thead>
                <tr>
                  <th>{t("analysis.period")}</th>
                  <th class="r">kWh</th>
                  <th class="r">{t("analysis.night")}</th>
                  <th class="r">{t("analysis.change")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t("analysis.thisPeriod")}</td>
                  <td class="r num">
                    {formatNumber(totalOf(props.now), locale(), 1)}
                  </td>
                  <td class="r num">
                    {formatShare(nightOf(props.now), locale())}
                  </td>
                  <td class="r">
                    <Show when={change() !== null} fallback="—">
                      <span class={`chip ${change()! > 0 ? "crit" : "ok"}`}>
                        {change()! > 0 ? "+" : ""}
                        {formatNumber(change()! * 100, locale(), 1)}%
                      </span>
                    </Show>
                  </td>
                </tr>
                <tr>
                  <td>{t("analysis.previousPeriod")}</td>
                  <td class="r num">
                    {formatNumber(totalOf(before()), locale(), 1)}
                  </td>
                  <td class="r num">
                    {formatShare(nightOf(before()), locale())}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
            <p class="hint" style={{ margin: "8px 0 0" }}>
              {t("analysis.compareHint", { unit: unitLabel(unit()) })}
            </p>
          </div>
        </>
      )}
    </Show>
  );
}

/** Week pattern of one series: days down, hours across, colour by value. */
function Heat(props: { data: PointSeries[] }) {
  const [which, setWhich] = createSignal(props.data[0]!.point.id);
  const s = () =>
    props.data.find((x) => x.point.id === which()) ?? props.data[0]!;
  const rows = createMemo(() => heatmap(s().values));
  const values = () =>
    rows().flatMap((r) => r.hours.filter((v): v is number => v !== null));
  const lo = () => Math.min(0, ...values());
  const hi = () => Math.max(...values());
  const shade = (v: number) =>
    Math.round(8 + ((v - lo()) / (hi() - lo() || 1)) * 92);
  const dayLabel = (day: number) =>
    new Intl.DateTimeFormat(locale(), {
      weekday: "short",
      day: "numeric",
      month: "numeric",
    }).format(new Date(day));
  return (
    <div class="card-b stack">
      <Show when={props.data.length > 1}>
        <label class="hint">
          {t("analysis.series")}{" "}
          <select
            value={which()}
            onChange={(e) => setWhich(e.currentTarget.value)}
          >
            <For each={props.data}>
              {(x) => <option value={x.point.id}>{x.point.name}</option>}
            </For>
          </select>
        </label>
      </Show>
      <Show
        when={rows().length > 0}
        fallback={<p class="hint">{t("overview.noData")}</p>}
      >
        <div class="heat" role="img" aria-label={t("analysis.type.heat")}>
          <span />
          <For each={[...Array(24).keys()]}>
            {(h) => (
              <span style={{ "text-align": "center" }}>
                {h % 3 === 0 ? h : ""}
              </span>
            )}
          </For>
          <For each={rows()}>
            {(r) => (
              <>
                <span style={{ "align-self": "center" }}>
                  {dayLabel(r.day)}
                </span>
                <For each={r.hours}>
                  {(v, h) => (
                    <Show
                      when={v !== null}
                      fallback={
                        <span
                          class="c g"
                          title={`${dayLabel(r.day)} ${h()}:00 · ${t("overview.noData")}`}
                        />
                      }
                    >
                      <span
                        class="c"
                        title={`${dayLabel(r.day)} ${String(h()).padStart(2, "0")}:00 · ${formatNumber(v, locale(), 2)} ${unitLabel(s().unit)}`}
                        style={{
                          background: `color-mix(in srgb, ${mediumColor(s().point.medium)} ${shade(v!)}%, var(--surface))`,
                        }}
                      />
                    </Show>
                  )}
                </For>
              </>
            )}
          </For>
        </div>
        <div
          style={{ display: "flex", gap: "10px", "align-items": "center" }}
          class="hint"
        >
          <span>{formatNumber(lo(), locale(), 1)}</span>
          <span
            style={{
              height: "8px",
              width: "160px",
              "border-radius": "4px",
              background: `linear-gradient(90deg,var(--surface),${mediumColor(s().point.medium)})`,
            }}
          />
          <span>
            {formatNumber(hi(), locale(), 1)} {unitLabel(s().unit)}
          </span>
          <span>· {t("analysis.heatHint")}</span>
        </div>
      </Show>
    </div>
  );
}

/** How long each series stays above each value (load duration curve). */
function DurationCurve(props: {
  data: PointSeries[];
  colorOf: (id: string) => string;
}) {
  const W = 800;
  const H = 240;
  const P = { l: 52, r: 12, t: 12, b: 26 };
  const unit = () => props.data[0]!.unit;
  const curves = createMemo(() =>
    props.data
      .filter((s) => s.unit === unit())
      .map((s) => ({ s, d: duration(s.values) }))
      .filter((c) => c.d !== null),
  );
  const top = () => Math.max(0.001, ...curves().map((c) => c.d!.max)) * 1.1;
  const X = (f: number) => P.l + (W - P.l - P.r) * f;
  const Y = (v: number) => P.t + (H - P.t - P.b) * (1 - v / top());
  return (
    <Show
      when={curves().length > 0}
      fallback={<div class="card-b hint">{t("overview.noData")}</div>}
    >
      <div class="card-b">
        <div class="chart">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={t("analysis.type.dur")}
          >
            <For each={[0, 0.25, 0.5, 0.75, 1]}>
              {(f) => (
                <>
                  <line
                    x1={X(f)}
                    x2={X(f)}
                    y1={P.t}
                    y2={H - P.b}
                    stroke="var(--line)"
                  />
                  <text x={X(f)} y={H - 8} text-anchor="middle">
                    {t("analysis.ofTime", { share: f * 100 })}
                  </text>
                  <line
                    x1={P.l}
                    x2={W - P.r}
                    y1={Y(top() * f)}
                    y2={Y(top() * f)}
                    stroke="var(--line)"
                    stroke-dasharray="2 3"
                  />
                  <text x={P.l - 6} y={Y(top() * f) + 3} text-anchor="end">
                    {formatNumber(top() * f, locale(), 1)}
                  </text>
                </>
              )}
            </For>
            <For each={curves()}>
              {(c) => {
                const v = c.d!.sorted;
                const step = Math.max(1, Math.floor(v.length / 400));
                const pts = v
                  .filter((_, i) => i % step === 0 || i === v.length - 1)
                  .map(
                    (x, i, a) => `${X(i / Math.max(1, a.length - 1))},${Y(x)}`,
                  );
                return (
                  <>
                    <polyline
                      points={pts.join(" ")}
                      fill="none"
                      stroke={props.colorOf(c.s.point.id)}
                      stroke-width="2"
                    />
                    <circle
                      cx={X(0.05)}
                      cy={Y(c.d!.p95)}
                      r="3.5"
                      fill={props.colorOf(c.s.point.id)}
                    />
                    <text
                      x={X(0.05) + 6}
                      y={Y(c.d!.p95) - 6}
                      style={{ fill: "var(--ink)" }}
                    >
                      P95: {formatNumber(c.d!.p95, locale(), 2)}{" "}
                      {unitLabel(unit())}
                    </text>
                  </>
                );
              }}
            </For>
          </svg>
        </div>
      </div>
      <div class="lg">
        <For each={curves()}>
          {(c) => (
            <span
              style={{
                display: "inline-flex",
                gap: "6px",
                "align-items": "center",
              }}
            >
              <i style={{ background: props.colorOf(c.s.point.id) }} />
              {t("analysis.durationLegend", {
                name: c.s.point.name,
                max: formatNumber(c.d!.max, locale(), 2),
                median: formatNumber(c.d!.median, locale(), 2),
                unit: unitLabel(unit()),
              })}
            </span>
          )}
        </For>
      </div>
      <div class="card-b" style={{ "padding-top": 0 }}>
        <p class="hint" style={{ margin: 0 }}>
          {t("analysis.durationHint")}
        </p>
      </div>
    </Show>
  );
}

/**
 * Daily energy of a meter against the daily average of a temperature point, with a least-squares line: the base
 * load and how much each degree adds. Both come from the selection.
 */
function Temperature(props: { data: PointSeries[] }) {
  const temps = () => props.data.filter((s) => s.attribute === "temperature");
  const others = () => props.data.filter((s) => s.attribute !== "temperature");
  const [tempId, setTempId] = createSignal<string>();
  const [otherId, setOtherId] = createSignal<string>();
  const temp = () => temps().find((s) => s.point.id === tempId()) ?? temps()[0];
  const other = () =>
    others().find((s) => s.point.id === otherId()) ?? others()[0];
  const daily = (s: PointSeries) =>
    s.energy.length > 0 ? total(s.energy, "1d") : average(s.values, "1d");
  const yUnit = () =>
    other() && other()!.energy.length > 0 ? "kWh" : unitLabel(other()?.unit);
  const points = createMemo(() =>
    temp() && other()
      ? pairs(average(temp()!.values, "1d"), daily(other()!))
      : [],
  );
  const fit = createMemo(() => linearFit(points()));
  const W = 800;
  const H = 280;
  const P = { l: 52, r: 12, t: 12, b: 30 };
  const xs = () => points().map((p) => p.x);
  const ys = () => points().map((p) => p.y);
  const x0 = () => Math.floor(Math.min(...xs()) - 1);
  const x1 = () => Math.ceil(Math.max(...xs()) + 1);
  const y1 = () => Math.max(...ys()) * 1.1 || 1;
  const X = (x: number) =>
    P.l + ((x - x0()) / (x1() - x0() || 1)) * (W - P.l - P.r);
  const Y = (y: number) => P.t + (1 - y / y1()) * (H - P.t - P.b);
  return (
    <Show
      when={temps().length > 0 && others().length > 0}
      fallback={<div class="card-b hint">{t("analysis.xyPick")}</div>}
    >
      <div class="note" style={{ "flex-wrap": "wrap", gap: "10px" }}>
        <label class="hint">
          {t("analysis.xyTemperature")}{" "}
          <select
            value={temp()!.point.id}
            onChange={(e) => setTempId(e.currentTarget.value)}
          >
            <For each={temps()}>
              {(s) => <option value={s.point.id}>{s.point.name}</option>}
            </For>
          </select>
        </label>
        <label class="hint">
          {t("analysis.xyQuantity")}{" "}
          <select
            value={other()!.point.id}
            onChange={(e) => setOtherId(e.currentTarget.value)}
          >
            <For each={others()}>
              {(s) => <option value={s.point.id}>{s.point.name}</option>}
            </For>
          </select>
        </label>
      </div>
      <Show
        when={points().length > 0}
        fallback={<div class="card-b hint">{t("analysis.xyNoDays")}</div>}
      >
        <div class="card-b">
          <div class="chart">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              role="img"
              aria-label={t("analysis.type.xy")}
            >
              <For each={[0, 0.25, 0.5, 0.75, 1]}>
                {(f) => (
                  <>
                    <line
                      x1={P.l}
                      x2={W - P.r}
                      y1={Y(y1() * f)}
                      y2={Y(y1() * f)}
                      stroke="var(--line)"
                      stroke-dasharray="2 3"
                    />
                    <text x={P.l - 6} y={Y(y1() * f) + 3} text-anchor="end">
                      {formatNumber(y1() * f, locale(), 1)}
                    </text>
                    <text
                      x={X(x0() + (x1() - x0()) * f)}
                      y={H - 10}
                      text-anchor="middle"
                    >
                      {formatNumber(x0() + (x1() - x0()) * f, locale(), 1)} °C
                    </text>
                  </>
                )}
              </For>
              <Show when={fit()}>
                {(f) => (
                  <line
                    x1={X(x0())}
                    x2={X(x1())}
                    y1={Y(f().slope * x0() + f().intercept)}
                    y2={Y(f().slope * x1() + f().intercept)}
                    stroke="var(--accent)"
                    stroke-width="2"
                  />
                )}
              </Show>
              <For each={points()}>
                {(p) => (
                  <circle
                    cx={X(p.x)}
                    cy={Y(p.y)}
                    r="5"
                    fill={mediumColor(other()!.point.medium)}
                    opacity="0.75"
                  >
                    <title>
                      {formatDateTime(new Date(p.t), locale())}:{" "}
                      {formatNumber(p.x, locale(), 1)} °C ·{" "}
                      {formatNumber(p.y, locale(), 2)} {yUnit()}
                    </title>
                  </circle>
                )}
              </For>
            </svg>
          </div>
        </div>
        <div class="card-b">
          <dl class="kv">
            <dt>{t("analysis.model")}</dt>
            <dd>
              <Show when={fit()} fallback={t("analysis.modelNone")}>
                {(f) =>
                  t("analysis.modelText", {
                    base: formatNumber(f().intercept, locale(), 2),
                    slope: formatNumber(f().slope, locale(), 3),
                    unit: yUnit(),
                    r2: formatNumber(f().r2, locale(), 2),
                    n: f().n,
                  })
                }
              </Show>
            </dd>
            <dt>{t("analysis.limits")}</dt>
            <dd>{t("analysis.limitsText")}</dd>
          </dl>
        </div>
      </Show>
    </Show>
  );
}

function StatsTable(props: {
  data: PointSeries[];
  colorOf: (id: string) => string;
}) {
  const from = () => range().from.getTime();
  const to = () => range().to.getTime();
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("analysis.stats")}</h2>
        <span class="meta">
          {formatRange(range().from, range().to, locale())}
        </span>
      </div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th />
              <th>{t("assets.point")}</th>
              <th class="r">{t("analysis.energy")}</th>
              <th class="r">{t("assets.average")}</th>
              <th class="r">{t("assets.maximum")}</th>
              <th class="r">{t("assets.minimum")}</th>
              <th class="r">{t("analysis.nightDay")}</th>
              <th class="r">{t("analysis.completeness")}</th>
            </tr>
          </thead>
          <tbody>
            <For each={props.data}>
              {(s) => {
                const st = stats(s.values, from(), to());
                const e = energyOf(s);
                const u = unitLabel(s.unit);
                return (
                  <tr>
                    <td style={{ width: "14px" }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: "10px",
                          height: "10px",
                          "border-radius": "2px",
                          background: props.colorOf(s.point.id),
                        }}
                      />
                    </td>
                    <td>{s.point.name}</td>
                    <td class="r num">
                      {e === null ? "—" : `${formatNumber(e, locale(), 1)} kWh`}
                    </td>
                    <td class="r num">
                      {formatNumber(st.avg, locale(), 2)} {u}
                    </td>
                    <td class="r num">{formatNumber(st.max, locale(), 2)}</td>
                    <td class="r num">{formatNumber(st.min, locale(), 2)}</td>
                    <td class="r">
                      <Show
                        when={
                          st.nightToDay !== null &&
                          s.point.role !== "environment"
                        }
                        fallback="—"
                      >
                        <span
                          class={`chip ${st.nightToDay! > 0.5 ? "warn" : ""}`}
                        >
                          {formatShare(st.nightToDay, locale())}
                        </span>
                      </Show>
                    </td>
                    <td class="r">
                      <Show when={st.coverage !== null} fallback="—">
                        <span
                          class={`chip ${st.coverage! < 0.99 ? "warn" : "ok"}`}
                        >
                          {formatShare(st.coverage, locale())}
                        </span>
                      </Show>
                    </td>
                  </tr>
                );
              }}
            </For>
          </tbody>
        </table>
      </div>
      <div class="card-b hint" style={{ "padding-top": "8px" }}>
        {t("analysis.statsHint")}
      </div>
    </section>
  );
}
